import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@clerk/react";
import { api, setTokenGetter } from "./api";

const Ctx = createContext(null);
const load = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; } };

export const ShopProvider = ({ children }) => {
  const { isSignedIn, getToken, isLoaded } = useAuth();
  const [cart, setCart] = useState(() => load("hw_cart", []));
  const [wishlist, setWishlist] = useState(() => load("hw_wish", []));
  const [compare, setCompare] = useState(() => load("hw_compare", []));
  const [recent, setRecent] = useState(() => load("hw_recent", []));
  const [me, setMe] = useState(null);
  const synced = useRef(false);

  useEffect(() => { setTokenGetter(isSignedIn ? () => getToken() : null); }, [isSignedIn, getToken]);
  useEffect(() => { localStorage.setItem("hw_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("hw_wish", JSON.stringify(wishlist)); }, [wishlist]);
  useEffect(() => { localStorage.setItem("hw_compare", JSON.stringify(compare)); }, [compare]);
  useEffect(() => { localStorage.setItem("hw_recent", JSON.stringify(recent)); }, [recent]);

  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn) { synced.current = false; setMe(null); return; }
    (async () => {
      const [{ data: profile }, { data: lists }] = await Promise.all([api.get("/me"), api.get("/me/lists")]);
      setMe(profile);
      setWishlist((w) => [...new Map([...lists.wishlist, ...w].map((x) => [x.slug, x])).values()]);
      setCart((c) => {
        const m = new Map(lists.cart.map((x) => [x.sku, x]));
        c.forEach((x) => m.set(x.sku, { ...m.get(x.sku), ...x }));
        return [...m.values()];
      });
      synced.current = true;
    })().catch((e) => console.error("Account sync failed", e));
  }, [isSignedIn, isLoaded]); // state setters are stable; re-run only when the auth state changes

  useEffect(() => {
    if (!synced.current) return;
    const id = setTimeout(() => { api.put("/me/cart", { items: cart }).catch((e) => console.error("Cart sync failed", e)); api.put("/me/wishlist", { items: wishlist }).catch((e) => console.error("Wishlist sync failed", e)); }, 600);
    return () => clearTimeout(id);
  }, [cart, wishlist]);

  const addToCart = useCallback((sku, qty = 1, slug) => setCart((c) => {
    const ex = c.find((x) => x.sku === sku);
    return ex ? c.map((x) => (x.sku === sku ? { ...x, qty: Math.min(50, x.qty + qty) } : x)) : [...c, { sku, qty, slug }];
  }), []);
  const setQty = useCallback((sku, qty) => setCart((c) => (qty < 1 ? c.filter((x) => x.sku !== sku) : c.map((x) => (x.sku === sku ? { ...x, qty: Math.min(50, qty) } : x)))), []);
  const replaceSku = useCallback((oldSku, newSku) => setCart((c) => c.map((x) => (x.sku === oldSku ? { ...x, sku: newSku } : x))), []);
  const clearCart = useCallback(() => setCart([]), []);
  const toggleWish = useCallback((slug) => {
    let added = false;
    setWishlist((w) => { added = !w.some((x) => x.slug === slug); return added ? [...w, { slug }] : w.filter((x) => x.slug !== slug); });
    return () => added;
  }, []);
  const inWish = (slug) => wishlist.some((x) => x.slug === slug);
  const toggleCompare = (slug) => {
    if (compare.includes(slug)) { setCompare(compare.filter((s) => s !== slug)); return true; }
    if (compare.length >= 4) return false;
    setCompare([...compare, slug]);
    return true;
  };
  const pushRecent = useCallback((item) => setRecent((r) => [item, ...r.filter((x) => x.slug !== item.slug)].slice(0, 12)), []);
  const cartCount = cart.reduce((a, b) => a + b.qty, 0);

  return (
    <Ctx.Provider value={{ cart, cartCount, addToCart, setQty, replaceSku, clearCart, wishlist, toggleWish, inWish, compare, toggleCompare, setCompare, recent, pushRecent, me, setMe, isSignedIn }}>
      {children}
    </Ctx.Provider>
  );
};

export const useShop = () => useContext(Ctx);
